import { useMemo, useState } from 'react';
import { slugify } from '../slugify';
import {
  countCardsInCategory,
  ensureCardsHaveCategoryKeys,
  normalizeCaseStudyCategories,
} from '../../content/caseStudyCategories';
import {
  createCaseStudyDetailTemplate,
  normalizeCaseStudyDetail,
} from '../../content/caseStudyDetailTemplate';
import AdminModal from '../components/AdminModal';
import ConfirmDialog from '../components/ConfirmDialog';
import AdminPagination from '../components/AdminPagination';
import AdminFilterTabs from '../components/AdminFilterTabs';
import AdminCollapsible from '../components/AdminCollapsible';
import AdminSteps from '../components/AdminSteps';
import AdminDetailPrompt from '../components/AdminDetailPrompt';
import { isCaseStudyDetailConfigured } from '../../content/caseStudyDetailTemplate';
import { usePagination } from '../hooks/usePagination';
import { useAdminPersist } from '../AdminPersistContext';
import { useEditorFeedback } from '../hooks/useEditorFeedback';
import { useNotifyFormDirty } from '../hooks/useNotifyFormDirty';

const PAGE_SIZE = 8;

const emptyCategory = () => ({
  key: `category-${Date.now()}`,
  name: 'New Category',
  iconUrl: '',
});

const emptyCard = (category) => {
  const id = `case-${Date.now()}`;
  const name = category?.name || 'AI Automation';
  return {
    id,
    categoryKey: category?.key || 'ai-automation',
    category: name,
    filterKey: name,
    company: 'Company Name',
    companySubtitle: '',
    title: 'Case study title',
    description: '',
    metrics: [{ value: '0%', label: 'Metric' }],
    href: `/case-studies/${id}`,
  };
};

export default function CaseStudiesEditor({ content, onChange }) {
  const [filterKey, setFilterKey] = useState('all');
  const [cardModalId, setCardModalId] = useState(null);
  const [cardModalTab, setCardModalTab] = useState('listing');
  const [categoryModal, setCategoryModal] = useState(null);
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);
  const [jsonError, setJsonError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cardSavedPrompt, setCardSavedPrompt] = useState(false);
  const [isNewCard, setIsNewCard] = useState(false);
  const { persistNow } = useAdminPersist();
  const feedback = useEditorFeedback({ persistNow });

  const categories = useMemo(
    () => normalizeCaseStudyCategories(content.caseStudies?.categories),
    [content.caseStudies?.categories]
  );
  const cards = useMemo(
    () => ensureCardsHaveCategoryKeys(content.caseStudies?.cards ?? [], categories),
    [content.caseStudies?.cards, categories]
  );
  const details = content.caseStudies?.details ?? {};
  const stats = content.caseStudies?.stats ?? [];

  const patchCaseStudies = (patch) => {
    onChange({
      ...content,
      caseStudies: {
        ...content.caseStudies,
        stats,
        cards,
        details,
        categories,
        ...patch,
      },
    });
  };

  const filteredCards = useMemo(() => {
    if (filterKey === 'all') return cards;
    const cat = categories.find((c) => c.key === filterKey);
    if (!cat) return cards;
    return cards.filter(
      (c) =>
        c.categoryKey === cat.key ||
        c.filterKey === cat.name ||
        c.category === cat.name
    );
  }, [cards, categories, filterKey]);

  const { page, setPage, totalPages, paginatedItems, totalItems } = usePagination(
    filteredCards,
    PAGE_SIZE
  );

  const filterTabs = useMemo(
    () => [
      { key: 'all', label: 'All', count: cards.length },
      ...categories.map((cat) => ({
        key: cat.key,
        label: cat.name,
        count: countCardsInCategory(cards, cat.key, cat.name),
      })),
    ],
    [cards, categories]
  );

  const active = cardModalId ? cards.find((c) => c.id === cardModalId) : null;
  const activeDetail = cardModalId ? details[cardModalId] : null;
  const editingCategory =
    categoryModal && categoryModal !== 'add'
      ? categories.find((c) => c.key === categoryModal)
      : categoryModal === 'add'
        ? emptyCategory()
        : null;

  const openAddCard = () => {
    const cat = categories.find((c) => c.key === filterKey) || categories[0];
    if (!cat) return;
    const card = emptyCard(cat);
    const template = createCaseStudyDetailTemplate(card.id, card.title);
    patchCaseStudies({
      cards: [...cards, card],
      details: { ...details, [card.id]: template },
    });
    setCardModalId(card.id);
    setCardModalTab('listing');
    setCardSavedPrompt(false);
    setIsNewCard(true);
  };

  const saveCard = (updated) => {
    const oldId = cardModalId;
    const cat = categories.find((c) => c.key === updated.categoryKey);
    const normalized = {
      ...updated,
      id: slugify(updated.id) || updated.id,
      category: cat?.name || updated.category,
      filterKey: cat?.name || updated.filterKey,
      href: `/case-studies/${slugify(updated.id) || updated.id}`,
    };
    let nextCards = cards.map((c) => (c.id === oldId ? normalized : c));
    if (oldId !== normalized.id) {
      nextCards = nextCards.filter((c) => c.id !== normalized.id).concat([normalized]);
    }
    let nextDetails = { ...details };
    if (oldId !== normalized.id && nextDetails[oldId]) {
      nextDetails[normalized.id] = nextDetails[oldId];
      delete nextDetails[oldId];
    }
    if (!nextDetails[normalized.id]) {
      nextDetails[normalized.id] = createCaseStudyDetailTemplate(normalized.id, normalized.title);
    }
    const catName = cat?.name || normalized.category;
    nextDetails[normalized.id] = normalizeCaseStudyDetail(
      {
        ...nextDetails[normalized.id],
        hero: { ...nextDetails[normalized.id].hero, primaryBadge: catName },
      },
      normalized.id
    );
    patchCaseStudies({ cards: nextCards, details: nextDetails });
    setCardModalId(normalized.id);
    setCardSavedPrompt(true);
    setIsNewCard(false);
    return true;
  };

  const discardCardModal = () => {
    if (isNewCard && cardModalId) {
      patchCaseStudies({
        cards: cards.filter((c) => c.id !== cardModalId),
        details: Object.fromEntries(Object.entries(details).filter(([k]) => k !== cardModalId)),
      });
    }
    setCardModalId(null);
    setCardSavedPrompt(false);
    setIsNewCard(false);
    feedback.setFormDirty(false);
  };

  const closeCardModal = () => {
    feedback.requestClose({ isNew: isNewCard, onDiscard: discardCardModal });
  };

  const closeCategoryModal = () => {
    feedback.requestClose({
      isNew: categoryModal === 'add',
      onDiscard: () => {
        setCategoryModal(null);
        feedback.setFormDirty(false);
      },
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'card') {
      patchCaseStudies({
        cards: cards.filter((x) => x.id !== deleteTarget.id),
        details: Object.fromEntries(Object.entries(details).filter(([k]) => k !== deleteTarget.id)),
      });
      if (cardModalId === deleteTarget.id) setCardModalId(null);
    } else if (deleteTarget.type === 'category') {
      patchCaseStudies({ categories: categories.filter((c) => c.key !== deleteTarget.id) });
      if (filterKey === deleteTarget.id) setFilterKey('all');
      if (categoryModal === deleteTarget.id) setCategoryModal(null);
    }
    setDeleteTarget(null);
  };

  return (
    <div className="admin-panel">
      <div className="admin-panel__head">
        <div>
          <h2>Case studies</h2>
          <p className="admin-panel__desc">
            Step 1: <strong>listing card</strong> on Case Studies. Step 2: full <strong>project detail page</strong>{' '}
            (/case-studies/…).
          </p>
        </div>
        <button
          type="button"
          className="admin-btn admin-btn--primary"
          onClick={openAddCard}
          disabled={!categories.length}
        >
          Add case study
        </button>
      </div>

      <AdminSteps />

      <AdminFilterTabs
        label="Category"
        tabs={filterTabs}
        activeKey={filterKey}
        onChange={(key) => {
          setFilterKey(key);
          setPage(1);
        }}
        extra={
          <button
            type="button"
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={() => setManageCategoriesOpen(true)}
          >
            Categories
          </button>
        }
      />

      <div className="admin-toolbar">
        <span className="admin-toolbar__meta">
          {totalItems} case study(s)
          {filterKey !== 'all' ? ` in ${categories.find((c) => c.key === filterKey)?.name}` : ''}
        </span>
      </div>

      <div className="admin-list">
        {paginatedItems.map((c) => {
          const detailReady = isCaseStudyDetailConfigured(details[c.id]);
          return (
          <div key={c.id} className="admin-list-item">
            <div>
              <strong>{c.title}</strong>
              {detailReady ? (
                <span className="admin-badge admin-badge--ok">Detail page ready</span>
              ) : (
                <span className="admin-badge admin-badge--warn">Needs detail page</span>
              )}
              <br />
              <span>
                {c.id} · {categories.find((cat) => cat.key === c.categoryKey)?.name || c.category}
              </span>
            </div>
            <div className="admin-list-actions">
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm"
                onClick={() => {
                  setCardModalId(c.id);
                  setCardModalTab('listing');
                  setCardSavedPrompt(false);
                  setJsonError('');
                  setIsNewCard(false);
                }}
              >
                Edit card
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm"
                onClick={() => {
                  setCardModalId(c.id);
                  setCardModalTab('detail');
                  setCardSavedPrompt(false);
                  setJsonError('');
                  setIsNewCard(false);
                }}
              >
                Detail page
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--danger admin-btn--sm"
                onClick={() => setDeleteTarget({ type: 'card', id: c.id })}
              >
                Remove
              </button>
            </div>
          </div>
        );
        })}
        {!paginatedItems.length ? (
          <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>No case studies in this category.</p>
        ) : null}
      </div>

      <AdminPagination
        page={page}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      <AdminCollapsible title="Optional: numbers on listing page" hint="e.g. 50+ Projects">
        <div className="admin-grid-2">
          {stats.map((stat, i) => (
            <div key={i} className="admin-field" style={{ marginBottom: 0 }}>
              <label>Number {i + 1}</label>
              <input
                value={stat.value}
                onChange={(e) => {
                  const next = [...stats];
                  next[i] = { ...next[i], value: e.target.value };
                  patchCaseStudies({ stats: next });
                }}
              />
              <label style={{ marginTop: 8 }}>Label</label>
              <input
                value={stat.label}
                onChange={(e) => {
                  const next = [...stats];
                  next[i] = { ...next[i], label: e.target.value };
                  patchCaseStudies({ stats: next });
                }}
              />
            </div>
          ))}
        </div>
      </AdminCollapsible>

      <AdminModal
        open={manageCategoriesOpen}
        onClose={() => {
          setManageCategoriesOpen(false);
          setCategoryModal(null);
        }}
        title="Manage categories"
        size="md"
      >
        <div className="admin-list" style={{ marginBottom: 16 }}>
          {categories.map((cat) => (
            <div key={cat.key} className="admin-list-item">
              <div>
                {cat.iconUrl ? (
                  <img src={cat.iconUrl} alt="" className="admin-preview-img admin-preview-img--square" />
                ) : null}
                <strong>{cat.name}</strong>
                <br />
                <span>{countCardsInCategory(cards, cat.key, cat.name)} case study(s)</span>
              </div>
              <div className="admin-list-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn--ghost admin-btn--sm"
                  onClick={() => {
                    setManageCategoriesOpen(false);
                    setCategoryModal(cat.key);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn--danger admin-btn--sm"
                  onClick={() => setDeleteTarget({ type: 'category', id: cat.key, name: cat.name })}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="admin-btn admin-btn--primary"
          onClick={() => {
            setManageCategoriesOpen(false);
            setCategoryModal('add');
          }}
        >
          Add category
        </button>
      </AdminModal>

      <AdminModal
        open={!!categoryModal}
        onClose={closeCategoryModal}
        title={categoryModal === 'add' ? 'Add category' : 'Edit category'}
        size="md"
      >
        {editingCategory ? (
          <CategoryForm
            category={editingCategory}
            categories={categories}
            onSave={(updated) => {
              const oldKey = categoryModal === 'add' ? null : categoryModal;
              let next;
              if (categoryModal === 'add') {
                next = [...categories, updated];
              } else {
                next = categories.map((c) => (c.key === oldKey ? updated : c));
              }
              const nextCards =
                oldKey && oldKey !== updated.key
                  ? cards.map((card) =>
                      card.categoryKey === oldKey
                        ? {
                            ...card,
                            categoryKey: updated.key,
                            category: updated.name,
                            filterKey: updated.name,
                          }
                        : card
                    )
                  : cards.map((card) =>
                      card.categoryKey === updated.key
                        ? { ...card, category: updated.name, filterKey: updated.name }
                        : card
                    );
              patchCaseStudies({ categories: next, cards: nextCards });
              setCategoryModal(null);
              feedback.notifySaved('Case study category saved to the server.');
            }}
            onCancel={closeCategoryModal}
            onDirtyChange={feedback.setFormDirty}
          />
        ) : null}
      </AdminModal>

      <AdminModal
        open={!!active}
        onClose={closeCardModal}
        title={active ? `Edit: ${active.title}` : 'Case study'}
        size="xl"
      >
        {active ? (
          <>
            <div className="admin-modal-tabs">
              <button
                type="button"
                className={
                  cardModalTab === 'listing' ? 'admin-modal-tab admin-modal-tab--active' : 'admin-modal-tab'
                }
                onClick={() => setCardModalTab('listing')}
              >
                1. Listing card
              </button>
              <button
                type="button"
                className={
                  cardModalTab === 'detail' ? 'admin-modal-tab admin-modal-tab--active' : 'admin-modal-tab'
                }
                onClick={() => setCardModalTab('detail')}
              >
                2. Project detail page
              </button>
            </div>

            {cardSavedPrompt && cardModalTab === 'listing' ? (
              <AdminDetailPrompt
                title="Listing card saved"
                message={`"${active.title}" appears on the Case Studies grid. Next, complete the full project page.`}
                buttonLabel="Open project detail editor"
                onOpenDetail={() => {
                  setCardModalTab('detail');
                  setCardSavedPrompt(false);
                }}
              />
            ) : null}

            {cardModalTab === 'listing' ? (
              <CaseCardForm
                card={active}
                categories={categories}
                embedded
                onSave={(form) => {
                  if (saveCard(form)) {
                    feedback.notifySaved('Case study listing card saved to the server.');
                  }
                }}
                onCancel={closeCardModal}
                onDirtyChange={feedback.setFormDirty}
              />
            ) : null}

            {cardModalTab === 'detail' ? (
              <>
                {!activeDetail ? (
                  <>
                    <p className="admin-panel__desc" style={{ marginBottom: 12 }}>
                      This project has no full page yet.
                    </p>
                    <button
                      type="button"
                      className="admin-btn admin-btn--primary"
                      onClick={() => {
                        patchCaseStudies({
                          details: {
                            ...details,
                            [cardModalId]: createCaseStudyDetailTemplate(cardModalId, active?.title),
                          },
                        });
                      }}
                    >
                      Create project page
                    </button>
                  </>
                ) : (
                  <>
                    <CaseDetailForm
                      detail={activeDetail}
                      onSave={(updated) => {
                        patchCaseStudies({
                          details: {
                            ...details,
                            [cardModalId]: normalizeCaseStudyDetail(updated, cardModalId),
                          },
                        });
                        feedback.notifySaved('Case study detail page saved to the server.');
                      }}
                      onDirtyChange={feedback.setFormDirty}
                    />
                    <AdminCollapsible title="Advanced: raw JSON" hint="Developers only">
                      <textarea
                        className="admin-json-editor"
                        id="cs-detail-json"
                        key={`json-${cardModalId}`}
                        defaultValue={JSON.stringify(activeDetail, null, 2)}
                      />
                      {jsonError ? <p className="admin-error">{jsonError}</p> : null}
                      <div className="admin-form-actions" style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost admin-btn--sm"
                          onClick={() => {
                            patchCaseStudies({
                              details: {
                                ...details,
                                [cardModalId]: createCaseStudyDetailTemplate(cardModalId, active?.title),
                              },
                            });
                          }}
                        >
                          Reset to default
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn--primary admin-btn--sm"
                          onClick={() => {
                            try {
                              const parsed = JSON.parse(document.getElementById('cs-detail-json').value);
                              patchCaseStudies({ details: { ...details, [cardModalId]: parsed } });
                              setJsonError('');
                            } catch (e) {
                              setJsonError(e.message);
                            }
                          }}
                        >
                          Save JSON
                        </button>
                      </div>
                    </AdminCollapsible>
                  </>
                )}
              </>
            ) : null}
          </>
        ) : null}
      </AdminModal>

      <ConfirmDialog
        open={!!deleteTarget}
        title={deleteTarget?.type === 'category' ? 'Remove category' : 'Remove case study'}
        message={
          deleteTarget?.type === 'category'
            ? `Remove category "${deleteTarget.name}"? Case studies in this category remain until you reassign them.`
            : 'Remove this case study and its detail page data?'
        }
        confirmLabel="Remove"
        danger
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {feedback.dialogs}
    </div>
  );
}

function CategoryForm({ category, categories, onSave, onCancel, onDirtyChange }) {
  const [form, setForm] = useState({ ...category });
  const [error, setError] = useState('');
  useNotifyFormDirty(form, category, onDirtyChange);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const key = slugify(form.key || form.name);
        if (!key) {
          setError('Category key is required.');
          return;
        }
        if (categories.some((c) => c.key === key && c.key !== category.key)) {
          setError('Another category already uses this key.');
          return;
        }
        onSave({ ...form, key, name: form.name.trim() || key });
      }}
    >
      {error ? <p className="admin-error">{error}</p> : null}
      <div className="admin-field">
        <label>Display name (filter tab)</label>
        <input value={form.name} onChange={(e) => set('name', e.target.value)} required />
      </div>
      <div className="admin-field">
        <label>Key (internal)</label>
        <input value={form.key} onChange={(e) => set('key', e.target.value)} />
      </div>
      <div className="admin-field">
        <label>Icon SVG / image URL (optional)</label>
        <input value={form.iconUrl || ''} onChange={(e) => set('iconUrl', e.target.value)} />
      </div>
      <div className="admin-form-actions">
        <button type="button" className="admin-btn admin-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="admin-btn admin-btn--primary">
          Save category
        </button>
      </div>
    </form>
  );
}

function CaseCardForm({ card, categories, embedded, onSave, onCancel, onDirtyChange }) {
  const [form, setForm] = useState({ ...card });
  const [error, setError] = useState('');
  useNotifyFormDirty(form, card, onDirtyChange);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onCategoryChange = (categoryKey) => {
    const cat = categories.find((c) => c.key === categoryKey);
    setForm((f) => ({
      ...f,
      categoryKey,
      category: cat?.name || f.category,
      filterKey: cat?.name || f.filterKey,
    }));
  };

  const metricsText = (form.metrics || []).map((m) => `${m.value}|${m.label}`).join('\n');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const id = slugify(form.id);
        if (!id) {
          setError('Id (slug) is required.');
          return;
        }
        const metrics = metricsText
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [value, ...rest] = line.split('|');
            return { value: (value || '').trim(), label: rest.join('|').trim() || 'Metric' };
          });
        onSave({ ...form, id, metrics });
      }}
    >
      {error ? <p className="admin-error">{error}</p> : null}
      <p className="admin-panel__desc" style={{ marginTop: 0 }}>
        Shown on the Case Studies <strong>listing grid</strong> — company, title, short text, and metrics.
      </p>
      <div className="admin-field">
        <label>Page URL name</label>
        <input value={form.id} onChange={(e) => set('id', e.target.value)} required />
      </div>
      <div className="admin-grid-2">
        <div className="admin-field">
          <label>Category</label>
          <select
            value={form.categoryKey || categories[0]?.key || ''}
            onChange={(e) => onCategoryChange(e.target.value)}
            required
          >
            {categories.map((c) => (
              <option key={c.key} value={c.key}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-field">
          <label>Company</label>
          <input value={form.company} onChange={(e) => set('company', e.target.value)} />
        </div>
      </div>
      <div className="admin-field">
        <label>Company subtitle</label>
        <input value={form.companySubtitle || ''} onChange={(e) => set('companySubtitle', e.target.value)} />
      </div>
      <div className="admin-field">
        <label>Title</label>
        <input value={form.title} onChange={(e) => set('title', e.target.value)} />
      </div>
      <div className="admin-field">
        <label>Short description on card</label>
        <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3} />
      </div>
      <div className="admin-field">
        <label>Metrics (one per line: value|label)</label>
        <textarea
          value={metricsText}
          onChange={(e) => {
            const metrics = e.target.value
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean)
              .map((line) => {
                const [value, ...rest] = line.split('|');
                return { value: (value || '').trim(), label: rest.join('|').trim() || 'Metric' };
              });
            set('metrics', metrics);
          }}
          rows={4}
        />
      </div>
      {!embedded ? (
        <div className="admin-form-actions">
          <button type="submit" className="admin-btn admin-btn--primary">
            Save card
          </button>
          <button type="button" className="admin-btn admin-btn--ghost" onClick={onCancel}>
            Close
          </button>
        </div>
      ) : (
        <div className="admin-form-actions">
          <button type="button" className="admin-btn admin-btn--ghost" onClick={onCancel}>
            Close
          </button>
          <button type="submit" className="admin-btn admin-btn--primary">
            Save listing card
          </button>
        </div>
      )}
    </form>
  );
}

function CaseDetailForm({ detail, onSave, onDirtyChange }) {
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(detail)));
  useNotifyFormDirty(form, detail, onDirtyChange);
  const setHero = (k, v) => setForm((f) => ({ ...f, hero: { ...f.hero, [k]: v } }));
  const setChallenge = (k, v) => setForm((f) => ({ ...f, challenge: { ...f.challenge, [k]: v } }));
  const setAlert = (k, v) => setForm((f) => ({ ...f, alert: { ...f.alert, [k]: v } }));
  const setPhase = (i, patch) =>
    setForm((f) => {
      const phases = [...(f.phases || [])];
      phases[i] = { ...phases[i], ...patch };
      return { ...f, phases };
    });

  const ensurePhases = () => {
    const phaseList = form.phases?.length >= 2 ? [...form.phases] : [...(form.phases || [])];
    while (phaseList.length < 2) {
      phaseList.push({
        title: `PHASE ${phaseList.length + 1}`,
        bodyLines: [''],
        imageSrc: '',
        imageAlt: '',
      });
    }
    return phaseList;
  };

  const phases = ensurePhases();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      <h4 className="admin-section-title">Hero</h4>
      <div className="admin-field">
        <label>Hero image URL</label>
        <input value={form.hero?.imageSrc || ''} onChange={(e) => setHero('imageSrc', e.target.value)} />
      </div>
      <div className="admin-grid-2">
        <div className="admin-field">
          <label>Primary badge</label>
          <input value={form.hero?.primaryBadge || ''} onChange={(e) => setHero('primaryBadge', e.target.value)} />
        </div>
        <div className="admin-field">
          <label>Secondary badge</label>
          <input value={form.hero?.secondaryBadge || ''} onChange={(e) => setHero('secondaryBadge', e.target.value)} />
        </div>
      </div>
      <div className="admin-field">
        <label>Hero title</label>
        <input value={form.hero?.title || ''} onChange={(e) => setHero('title', e.target.value)} />
      </div>
      <div className="admin-field">
        <label>Subtitle</label>
        <input value={form.hero?.subtitle || ''} onChange={(e) => setHero('subtitle', e.target.value)} />
      </div>
      <div className="admin-field">
        <label>Description</label>
        <textarea value={form.hero?.description || ''} onChange={(e) => setHero('description', e.target.value)} rows={2} />
      </div>

      <h4 className="admin-section-title">Challenge & alert</h4>
      <div className="admin-field">
        <label>Challenge heading</label>
        <input value={form.challenge?.heading || ''} onChange={(e) => setChallenge('heading', e.target.value)} />
      </div>
      <div className="admin-field">
        <label>Challenge body</label>
        <textarea value={form.challenge?.body || ''} onChange={(e) => setChallenge('body', e.target.value)} rows={3} />
      </div>
      <div className="admin-grid-2">
        <div className="admin-field">
          <label>Alert title</label>
          <input value={form.alert?.title || ''} onChange={(e) => setAlert('title', e.target.value)} />
        </div>
        <div className="admin-field">
          <label>Tech stack (comma-separated)</label>
          <input
            value={(form.techStack || []).join(', ')}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                techStack: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
              }))
            }
          />
        </div>
      </div>
      <div className="admin-field">
        <label>Alert body</label>
        <textarea value={form.alert?.body || ''} onChange={(e) => setAlert('body', e.target.value)} rows={2} />
      </div>

      <h4 className="admin-section-title">Phases</h4>
      {[0, 1].map((i) => (
        <div key={i} style={{ marginBottom: 16, padding: 12, background: '#f8f9fc', borderRadius: 8 }}>
          <strong>Phase {i + 1}</strong>
          <div className="admin-field" style={{ marginTop: 8 }}>
            <label>Title</label>
            <input value={phases[i]?.title || ''} onChange={(e) => setPhase(i, { title: e.target.value })} />
          </div>
          <div className="admin-field">
            <label>Body (one line per paragraph)</label>
            <textarea
              value={(phases[i]?.bodyLines || []).join('\n')}
              onChange={(e) =>
                setPhase(i, {
                  bodyLines: e.target.value.split('\n').filter((l) => l.length > 0),
                })
              }
              rows={4}
            />
          </div>
          <div className="admin-field">
            <label>Phase image URL</label>
            <input value={phases[i]?.imageSrc || ''} onChange={(e) => setPhase(i, { imageSrc: e.target.value })} />
          </div>
        </div>
      ))}

      <h4 className="admin-section-title">Impact metrics</h4>
      <div className="admin-field">
        <textarea
          rows={5}
          value={(form.impact || []).map((m) => `${m.value}|${m.label}|${m.footnote || ''}`).join('\n')}
          onChange={(e) => {
            const impact = e.target.value
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean)
              .map((line) => {
                const [value, label, footnote] = line.split('|');
                return {
                  value: (value || '').trim(),
                  label: (label || '').trim() || 'Metric',
                  footnote: (footnote || '').trim(),
                };
              });
            setForm((f) => ({ ...f, impact }));
          }}
          placeholder="78%|Faster response|Footnote text"
        />
      </div>

      <h4 className="admin-section-title">Long-term value</h4>
      <div className="admin-field">
        <label>Section title</label>
        <input
          value={form.ltsv?.title || ''}
          onChange={(e) => setForm((f) => ({ ...f, ltsv: { ...f.ltsv, title: e.target.value } }))}
        />
      </div>

      <div className="admin-form-actions">
        <button type="submit" className="admin-btn admin-btn--primary">
          Save detail page
        </button>
      </div>
    </form>
  );
}
